import { useCallback, useRef, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faEllipsis,
  faEllipsisVertical,
} from "@fortawesome/pro-regular-svg-icons";
import { Menu, MenuButton } from "@headlessui/react";
import { AnimatePresence } from "motion/react";
import { twJoin } from "tailwind-merge";

import {
  DropdownElement,
  isNav,
  OptionsDropdownElement,
  OptionsDropdownItems,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { IconButton, IconSize } from "src/design-system/Icon";
import { useHoverPopoverLock } from "src/hooks/useHoverPopover";
import { useOnClickOutside } from "src/hooks/usehooks-ts";

export type DropDownProps = {
  elements: OptionsDropdownElement[];
  disabled?: boolean;
  dataLoc?: string;
  buttonClassName?: string;
  timeoutDuration?: number;
  ellipsisDirection?: "vertical";
  icon?: IconProp;
  iconSize?: IconSize;
  placement?: "bottom start" | "bottom end";
  onOpenChanged?: (value: boolean) => void;
};

export const EllipsisOptionsDropdown: React.FC<DropDownProps> = ({
  elements,
  dataLoc,
  buttonClassName,
  ellipsisDirection = "vertical",
  icon,
  iconSize = "xs",
  timeoutDuration: timeout,
  placement = "bottom end",
  onOpenChanged,
  disabled = false,
}) => {
  const [open, _setOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const setOpen = (value: boolean) => {
    _setOpen(value);
    onOpenChanged?.(value);
  };

  // When this dropdown is rendered inside a hover popover, the parent provides
  // a lock so it stays visible while we're open — the menu items are portaled
  // outside the parent's hover area, so without this the parent would close
  // as soon as the cursor crossed onto our items. Driving the lock from a
  // ref-callback on the menu wrapper guarantees it's released even if the
  // dropdown unmounts while still open. Outside a hover popover the context
  // is null and this is a no-op.
  const hoverPopoverLock = useHoverPopoverLock();
  // keep-memo(react-compiler): a ref callback is re-run with null on every
  // identity change, which would release and re-take the lock each render.
  const setLockRef = useCallback(
    (node: HTMLDivElement | null) => {
      hoverPopoverLock?.setLocked(node !== null);
    },
    [hoverPopoverLock],
  );

  useOnClickOutside([menuRef, dropdownRef], () => {
    if (open) {
      setOpen(false);
    }
  });

  const getOnElementClick =
    (element: DropdownElement) => (event: React.MouseEvent<Element>) => {
      event.stopPropagation();
      if (!isNav(element)) {
        setOpen(false);
      }
      element.action();
    };

  // Inside a hover popover we skip the auto-close-on-mouse-leave: moving the
  // cursor between the menu and the parent popover would otherwise close the
  // menu and tear the parent down with it. The menu still closes via item
  // click, click-outside, or toggling the button.
  const renderedItems = (
    <div
      ref={setLockRef}
      onMouseLeave={
        hoverPopoverLock
          ? undefined
          : () => {
              setOpen(false);
            }
      }
    >
      <OptionsDropdownItems
        ref={dropdownRef}
        elements={elements}
        getOnElementClick={getOnElementClick}
        placement={placement}
        timeout={timeout}
        static
      />
    </div>
  );

  return (
    <Menu ref={menuRef} as="div">
      <div
        className={twJoin(
          "flex justify-center hover:text-gray-700",
          "outline-none transition-opacity duration-200",
          open && "text-gray-500 opacity-100",
          buttonClassName,
        )}
      >
        <MenuButton
          as={IconButton}
          dataLoc={dataLoc}
          // Since we have custom open state,
          // we need to set data attribute manually
          data-menu-open={String(open)}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
          icon={
            icon ??
            (ellipsisDirection === "vertical" ? faEllipsisVertical : faEllipsis)
          }
          size={iconSize}
        />
      </div>
      <AnimatePresence initial={false}>{open && renderedItems}</AnimatePresence>
    </Menu>
  );
};
